import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { paymentsApi } from '../api/payments/payments';
import type { PaymentInfo } from '../api/payments/payments.types';
import { useAuth } from './auth/auth-context';
import { isNative } from '../platform';

interface PaymentContextValue {
  paymentInfo: PaymentInfo | null;
  paymentLoading: boolean;
  fetchError: boolean;
  canAccessApp: boolean;
  isOnTrial: boolean;
  trialDaysRemaining: number | null;
  trialExpired: boolean;
  planName: string | null;
  refreshPayment: () => Promise<void>;
}

const PaymentContext = createContext<PaymentContextValue | undefined>(undefined);

const STALE_MS = 5 * 60 * 1000; // 5 minutes

const NATIVE_NOOP_VALUE: PaymentContextValue = {
  paymentInfo: null,
  paymentLoading: false,
  fetchError: false,
  canAccessApp: true,
  isOnTrial: false,
  trialDaysRemaining: null,
  trialExpired: false,
  planName: null,
  refreshPayment: async () => {},
};

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // On native apps, skip all payment/billing/trial logic entirely
  if (isNative()) {
    return (
      <PaymentContext.Provider value={NATIVE_NOOP_VALUE}>
        {children}
      </PaymentContext.Provider>
    );
  }

  return <WebPaymentProvider>{children}</WebPaymentProvider>;
};

const WebPaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const fetchedAtRef = useRef<number>(0);
  const mountedRef = useRef(true);

  const fetchPayment = useCallback(async () => {
    if (!profile?.id) return;
    try {
      setPaymentLoading(true);
      const info = await paymentsApi.getMe();
      if (mountedRef.current) {
        setPaymentInfo(info);
        setFetchError(false);
        fetchedAtRef.current = Date.now();
      }
    } catch (err) {
      // Don't clear existing payment info on transient errors
      console.error('Failed to fetch payment info:', err);
      if (mountedRef.current) {
        setFetchError(true);
      }
    } finally {
      if (mountedRef.current) {
        setPaymentLoading(false);
      }
    }
  }, [profile?.id]);

  // Listen for boot:payment event (seeded from combined boot endpoint)
  useEffect(() => {
    const handleBootPayment = (e: Event) => {
      const detail = (e as CustomEvent<PaymentInfo>).detail;
      if (detail && mountedRef.current) {
        setPaymentInfo(detail);
        setPaymentLoading(false);
        setFetchError(false);
        fetchedAtRef.current = Date.now();
      }
    };
    window.addEventListener('boot:payment', handleBootPayment);
    return () => window.removeEventListener('boot:payment', handleBootPayment);
  }, []);

  // Initial fetch when profile is loaded (skips if boot already seeded data)
  useEffect(() => {
    if (profile?.id) {
      if (!fetchedAtRef.current) {
        fetchPayment();
      }
    } else {
      setPaymentLoading(false);
    }
  }, [profile?.id, fetchPayment]);

  // Listen for payment:updated and payment:expired custom events
  useEffect(() => {
    const handleUpdated = () => {
      fetchPayment();
    };

    const handleExpired = () => {
      // Mark access as revoked immediately, then refresh
      setPaymentInfo((prev) =>
        prev ? { ...prev, can_access_app: false } : prev,
      );
      fetchPayment();
    };

    window.addEventListener('payment:updated', handleUpdated);
    window.addEventListener('payment:expired', handleExpired);
    return () => {
      window.removeEventListener('payment:updated', handleUpdated);
      window.removeEventListener('payment:expired', handleExpired);
    };
  }, [fetchPayment]);

  // Refresh on window focus if data is stale
  useEffect(() => {
    const handleFocus = () => {
      if (Date.now() - fetchedAtRef.current > STALE_MS) {
        fetchPayment();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchPayment]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fail-open: if payment info hasn't loaded yet or errored, don't lock out the user
  const canAccessApp = paymentInfo === null ? true : paymentInfo.can_access_app === true;
  const isOnTrial = paymentInfo?.is_on_trial === true;
  const trialExpired = paymentInfo?.trial_expired === true;
  const trialDaysRemaining = paymentInfo?.days_remaining ?? null;
  const planName = paymentInfo?.plan_name ?? null;

  return (
    <PaymentContext.Provider
      value={{
        paymentInfo,
        paymentLoading,
        fetchError,
        canAccessApp,
        isOnTrial,
        trialDaysRemaining,
        trialExpired,
        planName,
        refreshPayment: fetchPayment,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = (): PaymentContextValue => {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error('usePayment must be used within PaymentProvider');
  return ctx;
};
